import { useMemo } from "react";

import { useDownloadFolderQrZipMutation } from "@/graphql/folders/folders.hooks";
import { GraphqlMappedError } from "@/services/graphqlErrorService";

interface IUseDownloadFolderQrZipProps {
  onCompleted?: () => void;
  onError?: (error: GraphqlMappedError) => void;
}

interface IUseDownloadFolderQrZipVariables {
  folderId: number;
  extension: string;
  size: number;
}

// Generation can take a long time on large folders, so we don't poll for completion here — one
// mutation call, the caller reports "pending" to the user and lets them come back and re-trigger
// later, when the backend will hand back the cached/completed result.
const useDownloadFolderQrZip = ({
  onCompleted,
  onError,
}: IUseDownloadFolderQrZipProps = {}) => {
  const [mutate, { loading, error }] = useDownloadFolderQrZipMutation({
    context: { errorType: "local" },
    onCompleted: () => onCompleted?.(),
    onError: (e) => onError?.(GraphqlMappedError.from(e)),
  });

  return useMemo(
    () => ({
      downloadFolderQrZip: async ({
        folderId,
        extension,
        size,
      }: IUseDownloadFolderQrZipVariables) => {
        const { data } = await mutate({
          variables: { folderId, extension, size },
        });
        return data?.downloadFolderQrZip;
      },
      loading,
      error,
    }),
    [mutate, loading, error]
  );
};

export default useDownloadFolderQrZip;
